import React, { useRef, useState, useReducer, useEffect, forwardRef, useImperativeHandle } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { i18nIntl, formatNumber, adaptGameToContainer, getVerified, baseFake, isNotEmpty, isColorBright, getRemotedbUrl, colorToArray } from '../../lib/ep.js';
import QRCode from 'qrcode.react';
import { localizeFields } from '../builder/DynamicFields.js';
import { stage_promotion_types as spt } from '../../data/bootstrap_stages';

import ActionLink from '../builder/ActionLink';
import SaveButton from '../builder/SaveButton';
import ToggleButton from '../builder/ToggleButton';

import NextActionButton from '../games/NextActionButton';
import LeveledHTMLText from './helpers/LeveledHTMLText.js';

import BestScore from '../svg/BestScore';
import TimeCounterPerform from '../builder/TimeCounterPerform.js';
import EarnedPointsMessage from './loyalty/EarnedPointsMessage.js';

import Spinner from '../builder/Spinner.js';
import { getInferredFinalMessages } from '../games/StageFinalMessages.js';
import GameContainerHeader from './GameContainerHeader.js';

const InstructionsWrapper = ({ stage, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const button_label = (isNotEmpty(stage, 'instructions_button')) ? stage.instructions_button : i18n("Start");

	const instructions_placeholder = props?.instructions_placeholder ?? false;

	let instructions_description = (isNotEmpty(stage, 'instructions_description')) ? stage.instructions_description : false;
	if(instructions_description === false && instructions_placeholder !== false) {
		instructions_description = instructions_placeholder;
	}

	const instructions_as_game = props?.instructions_as_game ?? false;
	const instructions_at_top = props?.instructions_at_top ?? false;
	const instructions_has_border = props?.instructions_has_border ?? true;

	const instructions_classes = ['game_instructions', 'gui_main_text_color_on_bg', 'main_text_flex'];
	if(instructions_as_game) instructions_classes.push('as_game');

	const container_classes = ['instructions_contents'];
	if(!instructions_as_game) container_classes.push('graphic_instructions');
	if(!instructions_has_border) container_classes.push('without_border');
	if(props?.instructions_classes ?? false) container_classes.push(props.instructions_classes);

	return(
		<GameContainerWrapper>
			{(instructions_at_top && instructions_description !== false) && (
				<LeveledHTMLText
					_class={{ other:'fill_description not_in_bottom gui_main_text_color_on_bg' }}
				>{instructions_description}</LeveledHTMLText>
			)}

			<article class={instructions_classes.join(" ")}>
				{(stage.instructions_img) && (
					<div class="main_media_flex">
						<img class="instructions_img" src={stage.full_instructions_img} alt="" />
					</div>
				)}

				{(!instructions_at_top && instructions_description !== false) && (
					<LeveledHTMLText
						_class={{ other:'fill_description' }}
					>{instructions_description}</LeveledHTMLText>
				)}

				{(props.children != undefined) && (
					<div class={container_classes.join(" ")}>
						{props.children}
					</div>
				)}

				<div class="instructions_cta">
					<div class="gui_big_button flex_button">
						<SaveButton
							_fullclass=""
							onClick={(e, cb) => props.onInstructionsAccept(cb)}
							autotranslate={false}
						>{button_label}</SaveButton>
					</div>
				</div>
			</article>
		</GameContainerWrapper>
	)
};

const GameHeader = forwardRef((props, ref) => {
	const stage = props.stage;

	const display_countdown = props?.display_countdown ?? true;
	const display_score = props?.display_score ?? true;
	const display_attemps = props?.display_attemps ?? true;
	const custom_content = props?.custom_content ?? false;
	const display_custom_block = props?.display_custom_block ?? false;

	const game_attempts = parseInt(stage?.game_attempts ?? stage?.crush_min ?? 0);

	const [ timer_started, setTimerStarted ] = useState(props.auto_start_timer ?? true);

	const last_time = useRef(0);
	const last_score = useRef(0);

	const [ state, dispatch ] = useReducer((state, action) => {
		switch(action.type) {
			case 'SET_SCORE':
				const _object_score = { score:action.score };
				if(game_attempts != undefined) _object_score.attempts_left = parseInt(game_attempts) - action.moves;
				last_score.current = action.score;
				return {
					...state,
					..._object_score,
				};

			case 'SET_ATTEMPTS':
				return {
					...state,
					attempts_left:action.attempts_left,
				};

			default:
				return state;
		}
	}, {
		attempts_left:parseInt(game_attempts),
		score:0,
	});

	useEffect(() => {
		dispatch({ type:'SET_ATTEMPTS', attempts_left:game_attempts });
	}, [ game_attempts ]);

	useImperativeHandle(ref, () => ({
		setScore(score, moves) {
			if(game_attempts > 0 && moves == game_attempts) {
				let instance = props.timer.current;
				if(instance) {
					last_time.current = instance.getElapsedAndStop();
				}
			}

			dispatch({ type:'SET_SCORE', score, moves });
		},
		gameReady() {
			if(props.setInitGame == undefined) {
				// It will invalidate game. You better not get in here.
				setTimerStarted(true);
				return;
			}

			props.setInitGame(() => {
				setTimerStarted(true);
			});
		},
	}), []);

	const getTime = (score) => {
		let game_completed = false;
		let final_data = {};
		let reason = 'time_up';

		const success_pages = localizeFields(getInferredFinalMessages(props.stage), props.locale);
		if(parseInt(props.stage?.game_success_threshold ?? 0) > 0) {
			// WITH THRESHOLD
			if(last_score.current >= parseInt(props.stage?.game_success_threshold)) {
				game_completed = true;
				final_data = success_pages.find((page) => page.id == 'success');
			} else {
				game_completed = false;
				final_data = success_pages.find((page) => page.id == 'fail');
				reason = 'threshold';
				
			}
		} else {
			game_completed = props.no_time_bonus ? true : false;
		}
		debugger;
		if(props.onEnded != undefined) {
			props.onEnded(game_completed, final_data,reason);
		}
	};

	let attempts_highlight = 3;
	if(parseInt(stage.game_attempts) < 10) attempts_highlight = 1;

	return (
		<GameContainerHeader
			countdown={(display_countdown) ? (
				<TimeCounterPerform
					ref={props.timer}
					stopped={props.is_builder && !props.is_test || !timer_started}
					seconds={stage?.game_time ?? 60}
					onEnd={getTime}
					in_text={true}
					classes=''	// remove the "hasCountdown" class so it doesn't zoom the digits
				/>
			) : null}
			attempts={(display_attemps) ? <strong>{state.attempts_left}</strong> : null}
			hurry_up_attempts={(state.attempts_left < attempts_highlight + 1) ? true : false}
			score={(display_score) ? <strong>{state.score}</strong> : null}
			custom_block={display_custom_block}
			intl={props.intl}
		>{custom_content}</GameContainerHeader>
	)
});

const GameThreshold = forwardRef(({ stage, ...props }, ref) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const display = stage?.game_display_threshold ?? false;
	const threshold = parseInt(stage?.game_success_threshold ?? '0');

	const colors = props?.layout_props?.other ?? {};

	const [ current_score, setCurrentScore ] = useState(0);

	useImperativeHandle(ref, () => ({
		setScore(score) {
			setCurrentScore(parseInt(score));
		},
	}), []);

	if(display === false || threshold < 1) return null;

	const is_completed = (current_score >= threshold) ? true : false;

	const percent = Math.min(100, Math.ceil(100 * current_score / threshold));

	const bar_color = colorToArray(colors?.ribbon_closed_background_color ?? 'rgba(215, 35, 60, 1)');
	const completed_bar_color = colorToArray(colors?.ribbon_open_background_color ?? 'rgba(120, 195, 0, 1)');
	
	const bar_text_color = colorToArray(colors?.ribbon_open_text_color ?? 'rgba(255, 255, 255, 1)');
	const completed_bar_text_color = colorToArray(colors?.ribbon_closed_border_color ?? 'rgba(255, 255, 255, 1)');

	const main_classes = ['game_threshold'];
	if(is_completed) main_classes.push('completed');

	const label = (!is_completed) ? props.intl.formatMessage({ id:"Goal: {threshold} points" }, { threshold }) : i18n("Challenge achieved!");

	return (
		<div
			class={main_classes.join(" ")}
			style={{
				'--_pending_bar_color':bar_color,
				'--_completed_bar_color':completed_bar_color,
				'--_completed_bar_text_color':completed_bar_text_color,
			}}
		>
			<div class="bar_bg">
				<span class="alone_text">{label}</span>

				<span
					key='threshold_bar'	// we need the key so the animation in width persists instead of creating new item on state change
					class="bar"
					style={{ width:`${percent}%` }}
				/>

				<span
					class="overlapped_text"
					style={{
						color:`rgba(${bar_text_color}, 1)`,
						clipPath:`rect(0 ${percent}% 100% 0 round var(--_bar-height) var(--_bar-height))`,
					}}
				>{label}</span>
			</div>
		</div>
	)
});

export const FinalPage = ({ final_message, ...props }) => {
	const game = props?.game ?? {};
	const override_promo_styles = props?.override_promo_styles ?? false;

	let type;
	switch(final_message.id) {
		case 'partial_success':
			type = 'semi_ok';
			break

		case 'out_of_moves':
		case 'out_of_time':
		case 'fail':
			type = 'ko';
			break;

		case 'death':
			type = 'death';
			break;
	
		case 'success':
		default:
			type = 'ok';
			break
	}

	const classes = ['game_result', `is_${type}`];
	if(!override_promo_styles) classes.push('override_styles');
	if(props?.use_new_design ?? false) {
		classes.push('new_design');
	} else {
		classes.push('is_fullscreen relative_to_container');
	}

	const styles = {};
	if(final_message.color && !override_promo_styles) {
		classes.push('custom_color');

		styles.backgroundColor = final_message.color;
		if(isColorBright(final_message.color)) {
			styles['--ep-white-color'] = '0, 0, 0';
			styles['--link-color'] = '0, 0, 0';
			styles['--button-background-color'] = '0, 0, 0, 1';
			styles['--button-background-color-hover'] = '0, 0, 0, 0.8';
		} else {
			styles['--link-color'] = '255, 255, 255';
		}
		styles['--link-color-hover'] = 'var(--link-color)';
	}

	const final_text = final_message?.text ?? false;

	return (
		<GameContainerWrapper game_type={props.game_type}>
			<div class={classes.join(" ")} style={styles}>
				<div class="g">
					<AnimatedResultIcon
						status={type}
						image={final_message.full_image}
						custom_success_color={final_message.color}
						scoring_data={game.scores}
						override_promo_styles={override_promo_styles}
						participate_result={game.participate_result}
						multiparticipation={props.multiparticipation}
					/>

					{(final_text !== false) && (
						<LeveledHTMLText
							_class={{ other:'status_title' }}
						>
							{final_text.replace(/\n/g, '<br />')}
						</LeveledHTMLText>
					)}

					{(props.score_resumee != undefined) && (
						props.score_resumee
					)}
				</div>

				{props.children != undefined && (
					props.children
				)}
			</div>
		</GameContainerWrapper>
	)
};

const AnimatedResultIcon = (props) => {
	const status = props?.status ?? 'ok';
	const image = props?.image ?? false;

	if(image !== false) {
		return (
			<div class="icon_container is_image">
				<img src={image} alt="" style={{ maxWidth:'100%' }} />
			</div>
		)
	}

	const is_multiparticipation = props?.multiparticipation ?? false;

	let icon = '';
	switch(status) {
		case 'ok':
			icon = 'ep-thumbs-up-empty';
			break;

		case 'invalid':
			icon = 'ep-thumbs-down-empty';
			break;

		case 'ko':
		case 'semi_ok':
			icon = 'ep-delete-circle';
			break;

		case 'death':
			icon = 'knk-skull';
			break;
	}

	if(status == 'ok' && is_multiparticipation && (props?.participate_result?.best ?? false)) {
		return (
			<BestScore
				custom_success_color={props.custom_success_color}
				override_promo_styles={props?.override_promo_styles ?? false}
			/>
		)
	}

	if(icon === false) {
		return null;
	}

	return (
		<div class="icon_container">
			<div class={`icon ${icon}`} />
		</div>
	)
};

export const GameContainerWrapper = (props) => {
	const is_whole = props?.is_whole ?? true;
	const game_type = props?.game_type ?? false;

	const classes = ['main_contents_flex'];
	if(is_whole) classes.push('gui_game_whole');
	if(game_type !== false) classes.push(`is_${game_type}`);

	return (
		<section class={classes.join(" ")}>
			{props.children}
		</section>
	)
};

const ScoreResumee = (props) => {
	const stage = props?.stage ?? false;

	const hide_score_resumee = stage?.hide_score_resumee ?? false;
	if(hide_score_resumee) return null;

	const override_promo_styles = props?.override_promo_styles ?? false;

	const pr = props?.participate_result ?? { ranking:10 };
	const is_multiparticipation = props?.multiparticipation ?? false;
	const has_ranking = props?.has_ranking ?? false;
	const link = (stage === false) ? `/entries/${props.promo_id}` : `/entries/${props.promo_id}/#s:${stage.id}`;

	return (
		<div class="score_resumee">
			{(is_multiparticipation && pr != undefined && pr.ranking != undefined && has_ranking) && (
				<div class="position_in_ranking" style={(!override_promo_styles) ? { '--link-color':'var(--ep-white-color)' } : null}>
					<p><FormattedMessage id="You are in position {pos} in the {ranking}" values={{pos:<strong>{pr.ranking}</strong>, ranking:<ActionLink _class={{other:'original_color_item'}} _href={link}>Ranking</ActionLink>}} /></p>
				</div>
			)}

			<div class="game_resumee">
				<p>{props.title}:</p>

				<ul>
					{props.scoring_data.map((score, s) => {
						return (
							<li key={s}>
								<span>{score.label}:</span>
								<span>{score.value}</span>
							</li>
						)
					})}
				</ul>
			</div>
		</div>
	)
};

const GAME = {
	INSTRUCTIONS:'INSTRUCTIONS',
	//ERRORS:'ERRORS',
	VERIFYING:'VERIFYING',
	TIMEUP:'TIMEUP',
	GAMEPLAY:'GAMEPLAY',
	INVALID:'INVALID',
	FINAL:'FINAL',	// custom final_page
	SUCCESS:'SUCCESS',
	PARTIAL_SUCCESS:'PARTIAL_SUCCESS',
	FAIL:'FAIL',
	HITSLIMIT:'HITSLIMIT',
	READYTOSEND:'READYTOSEND'
}

const GameSettings = (props) => {
	const silence = props?.stage?.silence ?? false;

	const [ muted, setMuted ] = useState(silence);

	useEffect(() => {
		if(props.stage.silence !== false) setMuted(false);
	}, [ props.stage.silence ]);

	// we affect the global sound volume in case of muted ON:
	Howler.volume((muted || silence) ? 0 : 1);

	if(silence) return null;

	return (
		<div class="game_config_controls">
			<ToggleButton
				squared={true}
				active={!muted}
				icon='ep-sound-on'
				icon_off='ep-sound-off'
				onClick={() => setMuted(!muted)}
			/>
		</div>
	)
};

const GameContainer = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const game_header = useRef();
	const game_threshold = useRef();
	const score = useRef(0);
	const timer = useRef(0);
	const hitsRef = useRef(0);
	const responseResult = useRef(false);
	const canvasFinished = useRef(false);
	const initTime = useRef(0);
	const soundtrack = useRef(false);
	const soundtrack_loaded = useRef(false);
	const soundtrack_playing = useRef(false);
	const soundtrack_loaded_tries = useRef(0);
	const soundtrack_id = useRef(false);
	const points_per_extra_second = 10;

	let stage = props.stage.Stage;
	const gameCanvas = useRef();

	const display_audio_controls = props?.display_audio_controls ?? false;
	const auto_start_timer = props?.auto_start_timer ?? true;

	const default_start_section = (stage?.instructions_disabled ?? false) ? GAME.GAMEPLAY : GAME.INSTRUCTIONS;

	const localized_finals = stage?.final_messages != undefined ? localizeFields(stage.final_messages,props.locale):undefined;

	useEffect(() => {
		// Little hack to remove the forced "height:100%" in participate.ctp (needed for old games in GameWrapper):
		const main_container = document.getElementById('main');
		if(main_container) main_container.style.height = 'auto';
	}, []);

	const isNewDesign = () => {
		switch(parseInt(stage.promotion_type_id)) {
			case spt.PUZZLE.id:
			case spt.WORDSEARCH.id:
			case spt.MEMORY.id:
			case spt.HIDDEN_OBJECTS.id:
			case spt.MATCH_IT.id:
			case spt.MINESWEEPER.id:
				return false;
		}

		return true;
	};

	const is_smashit = [spt.SMASHIT.id].includes(parseInt(stage.promotion_type_id)) ? true : false;
	const no_time_bonus = [spt.CATCHIT.id, spt.SMASHIT.id].includes(parseInt(stage.promotion_type_id)) ? true : false;

	const use_new_design = isNewDesign();
	const override_promo_styles = stage?.override_promo_styles ?? false;

	// "true" if num moves is a game requirement to succeed (and not a limitation to fail the game):
	const is_moves_to_do = [spt.CRUSH.id].includes(parseInt(stage.promotion_type_id)) ? true : false;

	const game_attempts = parseInt(stage?.game_attempts ?? (stage?.crush_min ?? 0));

	const getScores = (success_game, score_props) => {
		const partial_success = (success_game != 2) ? true : false;

		const fake_success = {
			score_per_hits:formatNumber(300),
			score_bonus_time:formatNumber(52),
			score_total:formatNumber(352),
		};
		
		const fake_partial_success = {
			score_total:formatNumber(300),
		};

		const score_data = (score_props != undefined) ? score_props : ((partial_success) ? fake_success : fake_partial_success);

		const whole_stats = (is_smashit) ? false : true;

		const score = (whole_stats && partial_success && !no_time_bonus) ? [{
			label:i18n("score", 'ucfirst'),
			value:(success_game || partial_success) ? score_data.score_per_hits : 0,
		}] : [];

		if(whole_stats && partial_success && !no_time_bonus && (score_props?.no_time != true)) {
			score.push({
				label:i18n("Bonus time"),
				value:(success_game || partial_success) ? score_data.score_bonus_time : 0,
			});
		}

		score.push({
			label:i18n("Total score"),
			value:(success_game || partial_success) ? formatNumber(score_data.score_total, 2) : 0,
		});

		return score;
	};

	const getStartSection = (section) => {
		let start_section = null;
		switch(section) {
			case 'instructions':
			case 'main':
				start_section = default_start_section;
				break;

			case 'settings':
				start_section = GAME.GAMEPLAY;
				break;

			case 'custom_final':
				start_section = GAME.FINAL;
				break;

			case 'score':
				if(stage.display_status == 'success') start_section = GAME.SUCCESS;
				if(stage.display_status == 'fail_time') start_section = GAME.TIMEUP;
				if(stage.display_status == 'fail_attempts') start_section = GAME.HITSLIMIT;
				if(stage.display_status == 'fail') start_section = GAME.FAIL;
				if(stage.display_status == 'partial_success') start_section = GAME.PARTIAL_SUCCESS;
				break;
		}

		return start_section;
	};

	const section = props?.section ?? 'main';
	const is_builder = props?.is_builder ?? false;
	const start_section = (is_builder) ? getStartSection(section) : null;

	const is_test = props?.is_test ?? false;
	const promotion = props?.promotion ?? {};
	const game_type = props?.game_type ?? null;
	const timer_started = useRef(false);
	const hashRef = useRef("");

	const [ game, gameDispatch ] = useReducer((state, action) => {
		switch(action.type) {
			case 'SET_STATUS':
				let new_status = true;
				switch(stage.display_status) {
					case 'success':
						new_status = true;
						break;

					case 'partial_success':
						new_status = 2;
						break;
						
					default:
						new_status = false;
						break;
				}

				return {
					...state,
					state:action.state,
					scores:getScores(new_status),
				};
			
			case 'SET_VALID_GAME':
				return { ...state, state:GAME.SUCCESS};

			case 'SET_INVALID_GAME':
				return { ...state, state:GAME.INVALID };

			case 'SET_HASH':
				return { ...state, hash:action.hash };

			case 'START':
				hashRef.current = action.hash;
				return { ...state, 'state':GAME.GAMEPLAY, hash:action.hash };

			case 'SET_VERIFY':
				return { ...state, state:GAME.VERIFYING,solved:action.solved};

			case 'SET_SOLVED':
				return { ...state};

			case 'SET_READY_TO_SEND':
				return {...state, ready_to_sent:true}

			case 'RESULT':
				let fail_reason = GAME.FAIL;
				if(action.time >= parseInt(stage.game_time) && no_time_bonus === false) fail_reason = GAME.TIMEUP;
				if(action.attempts > 0 && action.attempts == game_attempts) fail_reason = GAME.HITSLIMIT;
				//fail_reason = action.time == stage.game_time ? GAME.TIMEUP : GAME.HITSLIMIT;

				let new_state = (action.success == true) ? GAME.SUCCESS : fail_reason;
				if(action.success == 2) new_state = GAME.PARTIAL_SUCCESS;

				const game_scores = action?.scores ?? getScores(action.success);

				const game_next_participation = action?.next_participation ?? false;
				const game_participate_result = action?.participate_result ?? {};

				if(game_participate_result?.loyalty != undefined && window.loyalty_tokens != undefined) {
					game_participate_result.loyalty.token = window.loyalty_tokens.tokens[game_participate_result?.loyalty.token_id]
					window?.menu_ref?.getWrappedInstance().setTokens({[game_participate_result?.loyalty.token_id]:game_participate_result?.loyalty.total});
				}

				return {
					...state,
					state:new_state,
					ready_to_sent:false,
					fail:!action.success,
					time:action.time,
					next_participation:game_next_participation,
					participate_result:game_participate_result,
					scores:game_scores,
					final_data:action.final_data,
				};

			case 'RESTART':
				hitsRef.current = 0;
				score.current = 0;
				timer_started.current = false;
				responseResult.current = false;
				canvasFinished.current = false;
				hashRef.current = "";

				return {
					...state,
					state:default_start_section,
					start:false,
					success:false,
					fail:false,
					solved:false,
					scores:getScores(true),
				};

			default:
				return state;
		}
	}, {
		state:(start_section == null) ? default_start_section : start_section,
		hash:'',
		next_participation:false,
		participate_result:{},
		scores:getScores(true),
	});

	useEffect(() => {
		if(is_builder && !is_test) return;
		if(!isNotEmpty(stage, 'full_game_soundtrack')) return;

		soundtrack.current = new Howl({
			src:stage.full_game_soundtrack,
			loop:true,
			//html5:true,
			onload:() => {
				soundtrack_loaded.current = true;
			},
		});
	}, []);

	useEffect(() => {
		gameDispatch({ type:'RESTART' });
	}, [ stage.reset ]);

	useEffect(() => {
		if(game.state != GAME.GAMEPLAY) return;
		if(is_test) return;
		if(props.stage.Stage?.instructions_disabled) onInstructionsAccept();
	}, [ game.state ]);

	useEffect(() => {
		gameDispatch({ type:'SET_STATUS', state:getStartSection(section) });
	}, [ stage.display_status, section ]);

	//let display_score = is_builder && section == 'score' || game.success || game.fail || (!game.solved && game.verify);
	//let display_instructions = is_builder && section == 'instructions' || !game.start && section!='settings';
	//let display_success = display_score && stage.display_status == 'success' || game.success;
	//let display_fail_time = (display_score && stage.display_status == 'fail_time') || game.fail || (!game.solved && game.verify);
	//let display_invalid_game = !game.valid;

	const onInstructionsAccept = () => {
		gameDispatch({ type:'START', hash:'' });
		initGame(res=>{
			initTime.current = Date.now();
			gameDispatch({ type:'START', hash:res.hash });
		});
	};

	const setInitGame = (cb) => {
		if(is_builder && !is_test) {
			if(cb != undefined) cb();
			return;
		}

		if(is_test) {
			setTimeout(() => {
				gameDispatch({ type:'START', hash:'' });
				if(cb != undefined) cb();
			}, 500);
			return;
		}
		initTime.current = (Date.now()-initTime.current)/1000;
		if(cb != undefined)cb();
		// initGame((res)=>{
		// 	gameDispatch({ type:'START', hash:res.hash });
		// 	if(cb != undefined)cb();
		// });
	};

	const initGame = (cb) => {
		$.post(getRemotedbUrl(`/promotions/action/${stage.promotion_id}`), {
			info:baseFake(getVerified({
				hash:props.gp_hash,
				stage_id:stage.id,
			})),
			type:'gamestart',
		}, (res) => {
			if(cb != undefined)cb(res);
		}, 'JSON');
	}

	const restartGame = () => {
		props.canPlayAgain(()=>{
			gameDispatch({ type:'RESTART' });
			if(props.onReset != undefined) props.onReset();
		});
	};

	const displayGameResult = (success_game, time, attempts, final_data, scores) => {
		if(responseResult.current == false) {
			gameDispatch({ type:'SET_VERIFY', solved:success_game });
			return;
		}

		if(responseResult.current == 'invalid') {
			gameDispatch({ type:'SET_INVALID_GAME' });
			return;
		}
		
		let next_participation = false;
		if(responseResult.current.next_participation != undefined) {
			next_participation = responseResult.current.next_participation;
			if(next_participation !== false && next_participation !== true) {
				next_participation = EPDateHumanize(EPDateDuration(EPDateDiff(next_participation)));
			}
		}

		gameDispatch({
			type:'RESULT',
			success:success_game,
			time,
			next_participation,
			participate_result:responseResult.current,
			scores,
			attempts,
			verify:false,
			final_data,
		});
	};

	const success = (success_game, score, time, attempts, final_data) => {
		const score_per_hits = formatNumber(score);
		const score_bonus_time  = getBonusPerTime(time);
		let score_total  = parseFloat(score_per_hits);
		if(success_game != 2 && success_game != false && !no_time_bonus) score_total += parseFloat(score_bonus_time);	// NOT "SUCCESS_PARTIAL"

		const points_on_fail = (success_game == false && (stage.type == 16 || stage.promotion_type_id == 68))
		const scores = getScores(success_game, { score_per_hits, score_bonus_time, score_total, no_time: points_on_fail });

		if(is_test) {
			setTimeout(() => {
				gameDispatch({
					type:'RESULT',
					success:success_game,
					time,
					attempts,
					next_participation:false,
					participate_result:{},
					scores,
					final_data,
				});
			}, 50);
			return;
		}

		if (is_builder) {
			gameDispatch({ type:'RESTART' });
			gameDispatch({ type:'SET_STATUS', state:start_section });
			return;
		}

		displayGameResult(success_game,time,attempts,final_data,scores);
	};

	const continueGame = (e) => {
		if(game.participate_result.url != undefined) {
			window.location.href = game.participate_result.url;
			return;
		}
	};

	const getBonusPerTime = (time) => {
		let points = 0;
		let remaining_seconds = stage.game_time - time;
		remaining_seconds = remaining_seconds > 0 ? remaining_seconds : 0;
		points += remaining_seconds * points_per_extra_second;
		return formatNumber(points, 2);
	};

	const getFinalPageClasses = (type) => {
		const classes = ['game_result', `is_${type}`];

		if(override_promo_styles) {
			classes.push('override_styles');
		}

		if(use_new_design) {
			classes.push('new_design');
			return classes.join(" ");
		}

		classes.push('is_fullscreen relative_to_container');
		return classes.join(" ");
	};

	const startSoundtrack = () => {
		if(is_builder && !is_test) return;
		if(!isNotEmpty(stage, 'full_game_soundtrack')) return;
		if(soundtrack.current === false) return;
		if(soundtrack_playing.current === true) return;

		if(soundtrack_loaded.current === false && soundtrack_loaded_tries.current < 5) {
			setTimeout(() => {
				soundtrack_loaded_tries.current++;
				startSoundtrack();
			}, 300);
			return;
		}

		soundtrack_id.current = soundtrack.current.play();
		soundtrack.current.seek(0);
		soundtrack.current.fade(0, 0.2, 20, soundtrack_id.current);
		soundtrack_playing.current = true;
	};

	const endSoundtrack = (force_execution = false) => {
		const apply_fadeout = ([GAME.VERIFYING, GAME.GAMEPLAY].includes(game.state)) ? false : true;
		if(force_execution === false && !apply_fadeout) return;

		if(is_builder && !is_test) return;
		if(!isNotEmpty(stage, 'full_game_soundtrack')) return;
		if(soundtrack.current === false) return;
		if(soundtrack_playing.current === false) return;

		soundtrack.current.fade(0.2, 0, 2000, soundtrack_id.current);
		soundtrack_playing.current = false;
	};

	//if(display_score && display_success) {
	const final_stage = { ...stage, ...(game?.final_data ?? {}) };
	const has_ranking = (promotion.public_entries != undefined && promotion.public_entries != '0') ? true : false;

	endSoundtrack();

	if(game.state == GAME.SUCCESS && props?.final_page == undefined) {
		let final_message = localized_finals?.find((f) => (f.id == 'success')) ?? {
			id:'success',
			text:stage?.game_feedback_ok,
			color:final_stage?.game_color_ok ?? 'rgba(120, 195, 0, 1)',
			image:final_stage?.game_final_image_ok ?? false,
			full_image:final_stage?.full_game_final_image_ok ?? false,
		};

		final_message.text = isNotEmpty(final_message, 'text') ? final_message.text : `<h1>${i18n("Good job!")}</h1>`;
		final_message.color = isNotEmpty(final_message, 'color') ? final_message.color : 'rgba(120, 195, 0, 1)';

		return (
			<FinalPage
				final_message={final_message}
				multiparticipation={props.multiparticipation}
				game={game}
				game_type={game_type}
				override_promo_styles={override_promo_styles}
				use_new_design={use_new_design}
				score_resumee={<>
					<EarnedPointsMessage
						loyalty={game?.participate_result?.loyalty}
						promotion={{Promotion:promotion}}
						locale={props.locale}
					/>

					<ScoreResumee
						title={i18n("Results")}
						stage={final_stage}
						scoring_data={game.scores}
						multiparticipation={props.multiparticipation}
						promo_id={promotion.id}
						has_ranking={has_ranking}
						override_promo_styles={override_promo_styles}
						participate_result={game.participate_result}
					/>
				</>}
			>
				<NextActionButton
					custom_color={final_message.color}
					multiparticipation={props.multiparticipation}
					login_type={promotion.login_type}
					status='ok'
					allow_retry={(game.participate_result.retries > 1) ? true : false}
					retries={game.participate_result.retries}
					prize={game.participate_result.prize}
					next_participation={game.next_participation}
					continueGame={() => continueGame()}
					restartGame={() => restartGame()}
					intl={props.intl}
					is_builder={is_builder}
					allow_ranking={(has_ranking && is_smashit) ? true : false}
					promotion_id={promotion.id}
					override_promo_styles={override_promo_styles}
				/>
			</FinalPage>
		)
	}

	if(game.state == GAME.PARTIAL_SUCCESS) {
		const status = game?.final_data?.success_display_type ?? 'semi_ok';

		let final_message = localized_finals?.find((f) => (f.id == 'partial_success')) ?? {
			id:'partial_success',
			text:stage?.game_feedback_ok,
			color:final_stage?.game_color_ok ?? 'rgba(120, 195, 0, 1)',
			image:final_stage?.game_final_image_ok ?? false,
			full_image:final_stage?.full_game_final_image_ok ?? false,
		};

		final_message.text = isNotEmpty(final_message, 'text') ? final_message.text : `<h1>${i18n("Good job!")}</h1>`;
		final_message.color = isNotEmpty(final_message, 'color') ? final_message.color : 'rgba(120, 195, 0, 1)';

		if(game?.final_data != undefined && Object.keys(game.final_data).length > 0) {
			final_message.text = game.final_data.game_feedback_ok;
			final_message.color = game.final_data.game_color_ok;
		}

		const custom_success_color = final_message.color;

		return (
			<FinalPage
				final_message={final_message}
				multiparticipation={props.multiparticipation}
				game={game}
				game_type={game_type}
				use_new_design={use_new_design}
				override_promo_styles={override_promo_styles}
				score_resumee={<>
					<EarnedPointsMessage
						loyalty={game?.participate_result?.loyalty}
						promotion={{Promotion:promotion}}
						locale={props.locale}
					/>

					<ScoreResumee
						title={i18n("Results")}
						stage={final_stage}
						scoring_data={game.scores}
						multiparticipation={props.multiparticipation}
						promo_id={promotion.id}
						has_ranking={has_ranking}
						participate_result={game.participate_result}
					/>
				</>}
			>
				<NextActionButton
					custom_color={custom_success_color}
					multiparticipation={props.multiparticipation}
					login_type={promotion.login_type}
					disallow_complete={(game?.final_data?.success_display_type == 'ko') ? true : false}
					status={status}
					allow_retry={(game.participate_result.retries > 1) ? true : false}
					retries={game.participate_result.retries}
					prize={game.participate_result.prize}
					next_participation={game.next_participation}
					continueGame={() => continueGame()}
					restartGame={() => restartGame()}
					intl={props.intl}
					is_builder={is_builder}
					allow_ranking={(has_ranking && is_smashit) ? true : false}
					promotion_id={promotion.id}
					override_promo_styles={override_promo_styles}
				/>
			</FinalPage>
		)
	}

	//if(display_invalid_game) {
	if(game.state == GAME.INVALID) {
		const hash = game?.hash ?? props.gp_hash;

		const custom_success_color = 'rgba(0, 0, 0, 1)';

		return (
			<GameContainerWrapper game_type={game_type}>
				<div style={{ backgroundColor:custom_success_color }}>
					<div class={getFinalPageClasses('ko') + ' is_verifying custom_color'} style={{ backgroundColor:custom_success_color }}>
						<div class="g">
							<AnimatedResultIcon
								status='invalid'
								custom_success_color={custom_success_color}
								override_promo_styles={override_promo_styles}
								multiparticipation={props.multiparticipation}
							/>

							<div class="is_qr not_in_top">
								<QRCode value={hash} size={150} />
							</div>

							<LeveledHTMLText
								_class={{ other:'status_title' }}
							>
								{`<h1>${i18n("Invalid game")}</h1>`}
							</LeveledHTMLText>
						</div>
					</div>
				</div>
			</GameContainerWrapper>
		)
	}

	if(game.state == GAME.VERIFYING) {
		const color_custom = (stage.game_color_ok_time && game.solved || stage.game_color_ko_time && !game.solved) ? ' custom_color' : '';

		const style = {
			backgroundColor:'transparent',
			'--button-text-color':'var(--text-color-sec)',
			'--text-color':'var(--text-color-sec)',
			'--p-font-family':'var(--secondary-font)',
		};

		return (
			<GameContainerWrapper game_type={game_type}>
				<div class={getFinalPageClasses('verifying') + color_custom} style={style}>
					<Spinner
						color='rgba(var(--text-color-sec))'
						style={{
							'--text-weight':'700',
							textTransform:'uppercase',
						}}
					>Verifying</Spinner>
				</div>
			</GameContainerWrapper>
		)
	}

	if(game.state == GAME.TIMEUP || game.state == GAME.FAIL || (game.fail == true && game.state != GAME.HITSLIMIT)) {
		let default_message = i18n("***Time's up!");
		if(game.state == GAME.FAIL) default_message = i18n("Oops!");

		let type_string = 'ko';
		if(game.state == GAME.TIMEUP) type_string = 'out_of_time';
		if(game.state == GAME.FAIL) type_string = 'fail';

		const final_message =  localized_finals?.find((f) => (f.id == type_string)) ?? {
			id:type_string,
			text:stage?.game_feedback_ko_time,
			color:final_stage?.game_color_ko_time ?? 'rgba(215, 35, 60, 1)',
			image:final_stage?.game_final_image_ko_time ?? false,
			full_image:final_stage?.full_game_final_image_ko_time ?? false,
		};

		final_message.text = isNotEmpty(final_message,'text') ? final_message.text : `<h1>${default_message}</h1>`;

		return (
			<FinalPage
				final_message={final_message}
				multiparticipation={props.multiparticipation}
				game={game}
				game_type={game_type}
				use_new_design={use_new_design}
				override_promo_styles={override_promo_styles}
				score_resumee={(final_stage.type == '16') ? (
					<ScoreResumee
						title={i18n("Results")}
						stage={final_stage}
						scoring_data={game.scores}
						multiparticipation={props.multiparticipation}
						promo_id={promotion.id}
						has_ranking={has_ranking}
						participate_result={game.participate_result}
					/>
				) : null}
			>
				<NextActionButton
					custom_color={final_message.color}
					multiparticipation={props.multiparticipation}
					status='ko' 
					allow_retry={(game.participate_result.retries > 1) ? true : false}
					retries={game.participate_result.retries}
					next_participation={game.next_participation}
					continueGame={() => continueGame()}
					restartGame={() => restartGame()}
					intl={props.intl}
					is_builder={is_builder}
					override_promo_styles={override_promo_styles}
				/>
			</FinalPage>
		)
	}

	if(game.state == GAME.HITSLIMIT) {
		let default_message = i18n("You ran out of moves!");
		let type_string = 'out_of_moves';
		const final_message = localized_finals?.find((f) => (f.id == type_string)) ?? {
			id:type_string,
			text:stage?.game_feedback_ko_attempts,
			color:final_stage?.game_color_ko_attempts ?? 'rgba(215, 35, 60, 1)',
			image:final_stage?.game_final_image_ko_attempts ?? false,
			full_image:final_stage?.full_game_final_image_ko_attempts ?? false,
		};

		final_message.text = isNotEmpty(final_message,'text') ? final_message.text : `<h1>${default_message}</h1>`;

		return (
			<FinalPage
				final_message={final_message}
				multiparticipation={props.multiparticipation}
				game={game}
				game_type={game_type}
				use_new_design={use_new_design}
				override_promo_styles={override_promo_styles}
			>
				<NextActionButton
					custom_color={final_message.color}
					multiparticipation={props.multiparticipation}
					status='ko' 
					allow_retry={(game.participate_result.retries > 1) ? true : false}
					retries={game.participate_result.retries}
					next_participation={game.next_participation}
					continueGame={() => continueGame()}
					restartGame={() => restartGame()}
					intl={props.intl}
					is_builder={is_builder}
					override_promo_styles={override_promo_styles}
				/>
			</FinalPage>
		)
	}

	const doHit = (successful=false) => {
		hitsRef.current++;
		game_header.current.setScore(score.current, hitsRef.current);
		if (game_threshold.current){
			game_threshold.current.setScore(score.current);
		} 
		if(hitsRef.current == game_attempts && !successful) {
			timer.current.getElapsedAndStop();
			if(!is_moves_to_do) onEnded(false);
		}
	};

	const forceStop = () => {
		timer.current.getElapsedAndStop();
	};

	const verifyGame = (solved,time,attempts,final_data,reason) => {
		game.hash = hashRef.current;
		const score_per_hits = formatNumber(score.current);
		const score_bonus_time  = getBonusPerTime(time);
		const hash = game?.hash ?? props.gp_hash;
		let score_total  = parseFloat(score_per_hits);
		if(solved != 2 && solved!=false && !no_time_bonus) score_total += parseFloat(score_bonus_time);	// NOT "SUCCESS_PARTIAL"

		if (!window?.features.includes('partial_score')) {
			const score_on_time = ((stage.type == 16 || stage.promotion_type_id=='68') && solved == false)
			score_total = (solved || reason == 'threshold' || score_on_time) ? score_total : 0;//End game for points
		}

		$.post(getRemotedbUrl(`/promotions/action/${stage.promotion_id}`), {
			page:`stage_${stage.id}`,
			type:'gameplay',
			info:baseFake(getVerified({ 
				timegame:stage.game_time, 
				time, 
				attempts, 
				solved, 
				hash, 
				initTime:initTime.current,
				score_total
			})),
		}, (result) => {
			const game_valid = (result.status == 0) ? false : true;

			if(game_valid) {
				if(result.status) {
					let str = time+''+game.hash;
					let enc = "";
					for (var i = 0; i < str.length; i++) {
						let c = (str[i].charCodeAt(0) << 2) % 123;
						if(c < 32) c += 32;
						enc += String.fromCharCode(c);
					}
					
					let send = { points:score_total, gp:game.hash, signedRequest:enc, time, attempts,hash:result?.hash };
					if([spt.FACEOFF.id,spt.PICK_FAVORITE.id, spt.LIKEDISLIKE.id].includes(parseInt(stage.promotion_type_id))) {
						send = final_data;
					}

					send.completed = solved;
	
					props.submitQuiz(score_total, send, (response) => {
						let next_participation = false;
						if(response.next_participation != undefined) {
							next_participation = response.next_participation;
							if(next_participation !== false && next_participation !== true) {
								next_participation = EPDateHumanize(EPDateDuration(EPDateDiff(next_participation)));
							}
						}
						responseResult.current = response;

						if(canvasFinished.current == true) {
							success(solved, score.current, time, attempts,final_data);
						}

					});
				}
				return;
			}

			responseResult.current = 'invalid';
			if(canvasFinished.current == true) {
				success(solved, score.current, time, attempts,final_data);
			}
		}, 'JSON');
	};

	const onScore = (sc) => {
		score.current = sc;
		if (game_threshold.current){
			game_threshold.current.setScore(score.current);
		} 
		if(game_header.current == undefined) return;
		game_header.current.setScore(sc, hitsRef.current);
	};

	const onEnded = (success_game, final_data = {}, reason) => {
		const canvas = (gameCanvas.current != undefined && gameCanvas.current._wrappedInstance != undefined) ? gameCanvas.current.getWrappedInstance() : gameCanvas.current;
		let time_seconds = 0;
		if (props?.display_countdown ?? true) {
			timer.current.getElapsedAndStop();
			time_seconds = timer.current.getElapsedInSeconds();
		}

		if(!is_test && !is_builder) {
			verifyGame(success_game, time_seconds, hitsRef.current, final_data,reason);
		}

		canvas.stopGame(success_game, (new_success) => {
			const game_end_type = new_success ?? success_game;
			canvasFinished.current = true;
			const seconds = (props?.display_countdown ?? true) ? timer.current?.getElapsedInSeconds() : 0;
			success(game_end_type, score.current,seconds , hitsRef.current, final_data);
		},reason);
	};

	const startTimer = () => {
		if(timer_started.current) return;

		timer_started.current = true;
		game_header?.current?.gameReady();

		startSoundtrack();
	};

	const theme = stage?.theme ?? 'light';
	const force_game_to_square = props?.force_game_to_square ?? true;

	const canvas_props = {
		stage,
		promotion,
		is_builder,
		is_test,
		is_playable:props.is_playable,
		adaptToContainer:adaptGameToContainer,
		doHit,
		forceStop,
		onScore,
		onEnded,
		startTimer,
		endSoundtrack,
		hitsRef,
		intl:props.intl,
		sound_fx:props.sound_fx,
		key:props.key,	// use this to force a full reload of the game
	};
	
	if(game.state == GAME.INSTRUCTIONS) {
		const instructions_as_game = props?.instructions_as_game ?? false;
		const instructions_at_top = props?.instructions_at_top ?? false;

		const has_instructions = props?.instructions ?? "";

		if(instructions_as_game) {
			return (
				<InstructionsWrapper
					game_type={game_type}
					stage={stage}
					onInstructionsAccept={onInstructionsAccept}
					fail_message={props.fail_message}
					instructions_placeholder={props.instructions_placeholder}
					instructions_as_game={true}
					instructions_at_top={instructions_at_top}
					instructions_has_border={props.instructions_has_border}
					instructions_classes={props.instructions_classes}
					intl={props.intl}
				>
					<GameContainerWrapper is_whole={false} game_type={game_type}>
						<section class="is_wrapper gui_pos_rel">
							<section class={`gui_game_wrapper is_${theme}`}>
								{(force_game_to_square) && (
									<img src={`${window.cdn_url}img/blank_1x1.png`} class="img_pusher" width="100%" alt="" />
								)}

								<div class={`canvas_content${(!force_game_to_square) ? ' gui_pos_rel_important' : ''}`}>
									{React.cloneElement(props.children, {
										...canvas_props,
										key:'instructions',
										ref:gameCanvas,
										game_header,
										is_instructions:true,
									})}
								</div>

								{(has_instructions !== false) && (
									<div class="overlayed_instructions">
										{(has_instructions != "") ? has_instructions : i18n("Missing intructions")}
									</div>
								)}
							</section>
						</section>
					</GameContainerWrapper>
				</InstructionsWrapper>
			)
		}

		return (
			<InstructionsWrapper
				game_type={game_type}
				stage={stage}
				onInstructionsAccept={onInstructionsAccept}
				fail_message={props.fail_message}
				instructions_placeholder={props.instructions_placeholder}
				instructions_has_border={props.instructions_has_border}
				instructions_classes={props.instructions_classes}
				intl={props.intl}
				children={(has_instructions !== false ? ((has_instructions != "") ? has_instructions : i18n("Missing intructions")) : null)}
			/>
		);
	}

	if(game.state == GAME.SUCCESS && props?.final_page) {
		// @jordi / @edgard REVISAR QUE NO L'HAGI LIAT FENT AQUEST CASE NOU
		const final_message = stage?.final_messages?.find((f) => (f.id == 'success')) ?? {
			text:stage?.game_feedback_ok,
			color:final_stage?.game_color_ok ?? 'rgba(120, 195, 0, 1)',
			image:final_stage?.game_final_image_ok ?? false,
			full_image:final_stage?.full_game_final_image_ok ?? false,
		};

		final_message.text = isNotEmpty(final_message, 'text') ? final_message.text : `<h1>${i18n("Good job!")}</h1>`;

		return (
			<section class="is_wrapper gui_pos_rel">
				<section class="gui_game_wrapper">
					<div class="canvas_content">
						<div class="game_final_page">
							{React.cloneElement(props.final_page, {
								...canvas_props,
								ref:gameCanvas,
								gameDispatch,
								game_header,
								user_data:game.participate_result,
							})}
						</div>
						
						<NextActionButton
							custom_color={final_message.color}
							multiparticipation={props.multiparticipation}
							login_type={promotion.login_type}
							status='ok'
							allow_retry={game.participate_result.retries > 1}
							retries={game.participate_result.retries}
							prize={game.participate_result.prize}
							next_participation={game.next_participation}
							continueGame={() => continueGame()}
							restartGame={() => restartGame()}
							intl={props.intl}
							is_builder={is_builder}
							allow_ranking={false}
							promotion_id={promotion.id}
							override_promo_styles={stage.override_promo_styles}
						/>
					</div>
				</section>
			</section>
		);
	}

	return (
		<GameContainerWrapper game_type={game_type}>
			<section class="is_wrapper gui_pos_rel">
				{/*(stage.display_title != undefined && stage.display_title != "") && (
					<p class="gui_main_text_color_on_bg game_title question_text" dangerouslySetInnerHTML={{ __html:stage.display_title }} />
				)*/}

				<GameHeader
					ref={game_header}
					theme={theme}
					stage={props.stage.Stage}
					auto_start_timer={auto_start_timer}
					promotion={props?.promotion?.Promotion ?? (props?.promotion ?? {})}
					is_builder={is_builder}
					is_test={is_test}
					hitsRef={hitsRef}
					timer={timer}
					onEnded={onEnded}
					intl={props.intl}
					locale={props.locale}
					no_time_bonus = {no_time_bonus}
					display_countdown={props.display_countdown}
					display_score={props.display_score}
					display_attemps={props?.display_attemps ?? ((game_attempts > 0) ? true : false)}
					display_custom_block={props.custom_block}
					attempts_icon={props.attempts_icon}
					setInitGame={setInitGame}
					custom_content={(props?.custom_header_content ?? false) ? React.cloneElement(props.custom_header_content, {
						onEnded,
						game
					}) : null}
				/>

				{(props?.introduction_content ?? false) && (
					<div class="game_introduction_content">{props.introduction_content}</div>
				)}

				{(props?.extra_top_content ?? false) && (
					<div class="game_extra_contents is_top">
						{React.cloneElement(props.extra_top_content, {
							gameCanvas,
						})}
					</div>
				)}

				<section class={`gui_game_wrapper is_${theme}`}>
					{(force_game_to_square) && (
						<img src={`${window.cdn_url}img/blank_1x1.png`} class="img_pusher" width="100%" alt="" />
					)}

					<div class={`canvas_content${(!force_game_to_square) ? ' gui_pos_rel_important' : ''}`}>
						{React.cloneElement(props.children, {
							...canvas_props,
							ref:gameCanvas,
							gameDispatch:gameDispatch,
							game_header,
						})}
					</div>
				</section>

				{(props.legend != undefined) && (
					React.cloneElement(props.legend, {
						gameCanvas,
					})
				)}

				<GameThreshold
					ref={game_threshold}
					stage={props.stage.Stage}
					layout_props={promotion?.layout_props ?? {}}
					intl={props.intl}
				/>

				{(props?.extra_content_above ?? false) && (
					<div class="game_extra_contents is_top is_in_footer">
						{React.cloneElement(props.extra_content_above, {
							gameCanvas,
						})}
					</div>
				)}

				{(props?.extra_content ?? false) && (
					<div class="game_extra_contents">
						{React.cloneElement(props.extra_content, {
							gameCanvas,
						})}
					</div>
				)}

				{(display_audio_controls) && (
					<GameSettings
						stage={props.stage.Stage}
					/>
				)}
			</section>
		</GameContainerWrapper>
	)
};

export default injectIntl(GameContainer);